import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/auth";
import { useSchools } from "@/hooks/useSchools";
import SchoolStatsCards from "@/components/admin/schools/SchoolStatsCards";
import SchoolManagementTable from "@/components/admin/schools/SchoolManagementTable";
import AddSchoolDialog from "@/components/admin/schools/AddSchoolDialog";
import ExtendSubscriptionDialog from "@/components/admin/subscriptions/ExtendSubscriptionDialog";
import SchoolDataView from "@/components/admin/schools/SchoolDataView";
import ViewLoginCredentialsDialog from "@/components/admin/credentials/ViewLoginCredentialsDialog";
import DeleteSchoolDialog from "@/components/admin/schools/DeleteSchoolDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Settings, School, GraduationCap, Shield } from "lucide-react";
import type { School as SchoolType } from "@/hooks/useSchools";
import ContentManagement from "@/pages/general/ContentManagement";
import { useNavigate } from "react-router-dom";
import UsersChart from "@/components/reports/charts/UsersChart";
import { Skeleton } from "@mantine/core";

const AdminDashboard = () => {
  const { user, userProfile, setUserProfile } = useAuth();
  const {
    schools,
    loading,
    deleting,
    createSchool,
    updateSchoolStatus,
    extendSubscription,
    deleteSchool,
    refetch,
  } = useSchools();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showSchoolData, setShowSchoolData] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<SchoolType | null>(null);
  const navigate = useNavigate();
  const handleToggleStatus = (schoolId: string) => {
    const school = schools.find((s) => s.id === schoolId);
    if (school) {
      const newStatus = school.status === "active" ? "inactive" : "active";
      updateSchoolStatus(schoolId, newStatus);
    }
  };

  const handleExtendSubscription = (school: SchoolType) => {
    setSelectedSchool(school);
    setShowExtendDialog(true);
  };

  const handleViewSchoolData = (school: SchoolType) => {
    setSelectedSchool(school);
    setShowSchoolData(true);
  };

  const handleViewCredentials = (school: SchoolType) => {
    setSelectedSchool(school);
    setShowCredentials(true);
  };

  const handleCredentialsRegenerated = () => {
    // إعادة جلب بيانات المدارس بدلاً من إعادة تحميل الصفحة
    refetch();
  };

  const handleDeleteSchool = (school: SchoolType) => {
    setSelectedSchool(school);
    setShowDeleteDialog(true);
  };

  const handleSchoolNav = (school: SchoolType) => {
    // setUserProfile({ ...userProfile, school_id: school?.id });
    console.log("user profile", userProfile);
    navigate("/schools" + "/" + school.id.toString());
  };

  const handleConfirmExtend = (months: number) => {
    if (selectedSchool) {
      extendSubscription(selectedSchool.id, months);
    }
    setShowExtendDialog(false);
    setSelectedSchool(null);
  };

  const handleConfirmDelete = () => {
    if (selectedSchool) {
      deleteSchool(selectedSchool.id);
    }
    setShowDeleteDialog(false);
    setSelectedSchool(null);
  };

  if (loading) {
  return (
    <Skeleton height={"600"} width={"100%"} about="جاري التحميل...">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    </Skeleton>
  );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 relative"
      dir="rtl"
    >
      {deleting && (
        <div className="fixed inset-0 bg-black/20 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
              <span className="text-lg font-medium">جاري حذف المدرسة...</span>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-xl font-bold text-white mb-2 bg-gradient-to-r from-school-navy/90 to-school-blue/90 backdrop-blur-sm px-6 py-4 rounded-xl shadow-lg border border-white/20 w-full text-left flex items-center gap-3">
            <Shield className="h-6 w-6" />
            لوحة التحكم الإدارية
          </h1>
        </div>
        {/* Stats Cards */}
        <div className="mb-8">
          <SchoolStatsCards schools={schools} />
        </div>{" "}
        <div className="m-8 mb-32">
          <UsersChart />
        </div>
        {/* Admin Tabs */}
        <Tabs defaultValue="schools" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger
              value="schools"
              className="flex items-center gap-2 flex-row-reverse animate-fade-in"
            >
              <School className="h-4 w-4" />
              إدارة المدارس
            </TabsTrigger>
            <TabsTrigger
              value="content"
              className="flex items-center gap-2 flex-row-reverse animate-fade-in"
            >
              <Settings className="h-4 w-4" />
              إدارة المحتوى
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schools">
            {/* Schools Management Section */}
            <div className="space-y-6">
              <div>
                <div className="bg-white rounded-xl shadow-lg border border-gray-200">
                  <div className="bg-gradient-to-r from-school-navy/90 to-school-blue/90 rounded-t-xl p-6">
                    <div className="flex justify-between items-center">
                      <Button
                        onClick={() => setShowAddDialog(true)}
                        className="flex items-center gap-2 flex-row-reverse hover-scale bg-white/20 border-white/30 text-white hover:bg-white/30 backdrop-blur-sm"
                      >
                        <Plus className="h-4 w-4" />
                        إضافة مدرسة جديدة
                      </Button>
                      <h2 className="text-lg font-bold text-white flex items-center gap-3 flex-row-reverse">
                        <GraduationCap className="h-5 w-5" />
                        إدارة المدارس
                      </h2>
                    </div>
                  </div>
                  <div className="p-6">
                    <SchoolManagementTable
                      schools={schools}
                      onToggleStatus={handleToggleStatus}
                      onExtendSubscription={handleExtendSubscription}
                      onViewSchoolData={handleViewSchoolData}
                      onViewCredentials={handleViewCredentials}
                      onDeleteSchool={handleDeleteSchool}
                      onSchoolNav={handleSchoolNav}
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="content">
            <ContentManagement />
          </TabsContent>
        </Tabs>
      </div>

      <AddSchoolDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onAddSchool={createSchool}
      />

      <ExtendSubscriptionDialog
        open={showExtendDialog}
        onOpenChange={setShowExtendDialog}
        school={selectedSchool}
        onConfirm={handleConfirmExtend}
      />
      {selectedSchool && (
        <SchoolDataView
          open={showSchoolData}
          onOpenChange={setShowSchoolData}
          school={selectedSchool}
        />
      )}

      <ViewLoginCredentialsDialog
        open={showCredentials}
        onOpenChange={setShowCredentials}
        school={selectedSchool}
        onCredentialsRegenerated={handleCredentialsRegenerated}
      />

      <DeleteSchoolDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        school={selectedSchool}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
      />
    </div>
  );
};

export default AdminDashboard;
